import { useState, useRef } from 'react'
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { customerApi } from '../../services/api'
import toast from 'react-hot-toast'

export default function CSVImport({ isOpen, onClose, onImported }) {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState([])
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState(null)
  const fileRef = useRef(null)

  if (!isOpen) return null

  const handleFileChange = (e) => {
    const f = e.target.files[0]
    if (!f) return
    setFile(f)
    setResult(null)

    // Preview first 3 rows
    const reader = new FileReader()
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n').filter(Boolean)
      setPreview(lines.slice(0, 4)) // header + 3 rows
    }
    reader.readAsText(f)
  }

  const handleImport = async () => {
    if (!file) return
    setImporting(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await customerApi.post('/customer/knowledge-base/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setResult(res.data)
      toast.success(`${res.data.entries_imported} FAQs imported`)
      if (onImported) onImported()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">Import FAQs from CSV</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
            CSV must have <code className="font-mono bg-slate-200 px-1 rounded">question</code> and{' '}
            <code className="font-mono bg-slate-200 px-1 rounded">answer</code> columns. Optional:{' '}
            <code className="font-mono bg-slate-200 px-1 rounded">category</code>.
          </div>

          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-brand-300 transition-colors"
          >
            <ArrowUpTrayIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">{file ? file.name : 'Click to select CSV file'}</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {preview.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-3 overflow-x-auto">
              <p className="text-xs font-medium text-slate-700 mb-2">Preview:</p>
              {preview.map((line, i) => (
                <p key={i} className={`text-xs font-mono ${i === 0 ? 'text-slate-500 font-semibold' : 'text-slate-600'} truncate`}>
                  {line}
                </p>
              ))}
            </div>
          )}

          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-800 font-medium">{result.entries_imported} entries imported</p>
              {result.errors?.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-red-600 font-medium">{result.errors.length} errors:</p>
                  {result.errors.slice(0, 5).map((err, i) => (
                    <p key={i} className="text-xs text-red-500">Row {err.row}: {err.error}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex space-x-3 pt-2">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-200 text-sm font-medium text-slate-700 rounded-xl hover:bg-slate-50 transition-colors">
              {result ? 'Close' : 'Cancel'}
            </button>
            {!result && (
              <button
                onClick={handleImport}
                disabled={!file || importing}
                className="flex-1 px-4 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {importing ? 'Importing...' : 'Import'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
